// src/app/services/barcode/code128.service.ts
import { Injectable } from '@angular/core';

export interface BarcodeOptions {
  width?: number;
  height?: number;
  displayValue?: boolean;
  fontSize?: number;
  textAlign?: string;
  margin?: number;
  background?: string;
  lineColor?: string;
}

@Injectable({
  providedIn: 'root'  // ⬅️ Disponible globalement
})
export class Code128Service {
  private static readonly CODE128 = [
    '11011001100', '11001101100', '11001100110', '10010011000', '10010001100',
    '10001001100', '10011001000', '10011000100', '10001100100', '11001001000',
    '11001000100', '11000100100', '10110011100', '10011011100', '10011001110',
    '10111001100', '10011101100', '10011100110', '11001110010', '11001011100',
    '11001001110', '11011100100', '11001110100', '11101101110', '11101001100',
    '11100101100', '11100100110', '11101100100', '11100110100', '11100110010',
    '11011011000', '11011000110', '11000110110', '10100011000', '10001011000',
    '10001000110', '10110001000', '10001101000', '10001100010', '11010001000',
    '11000101000', '11000100010', '10110111000', '10110001110', '10001101110',
    '10111011000', '10111000110', '10001110110', '11101110110', '11010001110',
    '11000101110', '11011101000', '11011100010', '11011101110', '11101011000',
    '11101000110', '11100010110', '11101101000', '11101100010', '11100011010',
    '11101111010', '11001000010', '11110001010', '10100110000', '10100001100',
    '10010110000', '10010000110', '10000101100', '10000100110', '10110010000',
    '10110000100', '10011010000', '10011000010', '10000110100', '10000110010',
    '11000010010', '11001010000', '11110111010', '11000010100', '10001111010',
    '10100111100', '10010111100', '10010011110', '10111100100', '10011110100',
    '10011110010', '11110100100', '11110010100', '11110010010', '11011011110',
    '11011110110', '11110110110', '10101111000', '10100011110', '10001011110',
    '10111101000', '10111100010', '11110101000', '11110100010', '10111011110',
    '10111101110', '11101011110', '11110101110', '11010000100', '11010010000',
    '11010011100', '1100011101011'
  ];

  encode(text: string): number[] {
    const encoded: number[] = [];
    encoded.push(104); // Start B

    let checksum = 104;

    for (let i = 0; i < text.length; i++) {
      const charCode = text.charCodeAt(i);
      let value: number;

      if (charCode >= 32 && charCode <= 126) {
        value = charCode - 32;
      } else {
        throw new Error(`Caractère non supporté: ${text[i]}`);
      }

      encoded.push(value);
      checksum += value * (i + 1);
    }

    encoded.push(checksum % 103);
    encoded.push(106); // Stop

    return encoded;
  }

  toSVG(text: string, options: BarcodeOptions = {}): string {
    const {
      width = 2,
      height = 100,
      displayValue = true,
      fontSize = 20,
      margin = 10,
      background = '#ffffff',
      lineColor = '#000000'
    } = options;

    const encoded = this.encode(text);
    let barcode = '';

    for (const value of encoded) {
      barcode += Code128Service.CODE128[value];
    }

    const totalWidth = barcode.length * width + 2 * margin;
    const totalHeight = height + (displayValue ? fontSize + 10 : 0) + 2 * margin;

    let svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${totalWidth}" height="${totalHeight}">`;
    svg += `<rect width="${totalWidth}" height="${totalHeight}" fill="${background}"/>`;

    let x = margin;
    for (const bit of barcode) {
      if (bit === '1') {
        svg += `<rect x="${x}" y="${margin}" width="${width}" height="${height}" fill="${lineColor}"/>`;
      }
      x += width;
    }

    if (displayValue) {
      const textY = height + margin + fontSize;
      svg += `<text x="${totalWidth / 2}" y="${textY}" font-family="monospace" font-size="${fontSize}" text-anchor="middle" fill="${lineColor}">${text}</text>`;
    }

    svg += '</svg>';
    return svg;
  }

  toDataURL(text: string, options: BarcodeOptions = {}): string {
    const svg = this.toSVG(text, options);
    return 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svg)));
  }
}
